// ===========================================
// Reports API Functions
// ===========================================
// API calls for attendance, revenue, student progress, and teacher performance reports
// Phase 2 Priority 12: Reporting & Analytics

import { apiClient } from '../services/api';

// ===========================================
// FILTER TYPES
// ===========================================

export interface AttendanceReportFilters {
  termId?: string;
  lessonId?: string;
  teacherId?: string;
  studentId?: string;
  startDate?: string; // ISO date string
  endDate?: string; // ISO date string
}

export interface RevenueReportFilters {
  termId?: string;
  startDate?: string;
  endDate?: string;
  status?: 'SENT' | 'PAID' | 'PARTIALLY_PAID' | 'OVERDUE' | 'ALL';
}

export interface StudentProgressFilters {
  termId?: string;
  studentId?: string;
  teacherId?: string;
  instrumentId?: string;
  programLevelId?: string;
  startDate?: string;
  endDate?: string;
}

export interface TeacherPerformanceFilters {
  termId?: string;
  teacherId?: string;
  startDate?: string;
  endDate?: string;
}

export interface HybridBookingReportFilters {
  termId?: string;
  lessonId?: string;
  weekNumber?: number;
  startDate?: string;
  endDate?: string;
}

export interface EnrollmentReportFilters {
  termId?: string;
  lessonTypeId?: string;
  teacherId?: string;
  programLevelId?: string;
}

// ===========================================
// SUMMARY DATA TYPES
// ===========================================

export interface AttendanceReportSummary {
  totalLessons: number;
  totalAttendanceRecords: number;
  presentCount: number;
  absentCount: number;
  excusedCount: number;
  attendanceRate: number; // Percentage (0-100)
  periodStart: string;
  periodEnd: string;
}

// Mirrors the backend's RevenueReportSummary exactly
// (apps/backend/src/services/reports/reports.types.ts). This interface used to
// declare seven fields the backend never emitted (totalInvoiced,
// averageInvoiceAmount, totalOverdue, ...), so every consumer silently read
// `undefined` — the response is type-asserted at the apiClient boundary, never
// validated, so tsc could not catch it.
export interface RevenueReportSummary {
  totalRevenue: number; // In cents (all invoices except DRAFT/CANCELLED)
  totalCollected: number; // In cents (PAID invoices)
  totalOutstanding: number; // In cents (SENT/OVERDUE/PARTIALLY_PAID)
  averageInvoiceValue: number; // In cents
  invoiceCount: number;
}

export interface StudentProgressSummary {
  totalStudents: number;
  averageLessonsAttended: number;
  averageAttendanceRate: number; // Percentage (0-100)
  totalLessonsDelivered: number;
  averageProgressRate: number; // Percentage (0-100)
  totalPiecesCompleted: number;
  averageStreak: number; // Days
}

export interface TeacherPerformanceSummary {
  totalTeachers: number;
  totalLessonsDelivered: number;
  averageAttendanceRate: number; // Percentage (0-100)
  totalStudentsTaught: number;
  totalRevenueGenerated: number; // In cents
  averageRevenuePerTeacher: number; // In cents
  /** Outstanding teacher tasks THIS WEEK — the dashboard's definition, not the report's date range. */
  outstandingTasks: OutstandingTaskCounts;
}

export interface OutstandingTaskCounts {
  total: number;
  attendance: number;
  classNotes: number;
  studentNotes: number;
}

// ===========================================
// DETAILED DATA TYPES
// ===========================================

export interface AttendanceReportRecord {
  lessonId: string;
  lessonName: string;
  teacherName: string;
  totalLessons: number;
  presentCount: number;
  absentCount: number;
  excusedCount: number;
  attendanceRate: number; // Percentage (0-100)
  studentCount: number;
}

export interface OutstandingInvoice {
  invoiceId: string;
  invoiceNumber: string;
  familyName: string;
  total: number; // In cents
  amountPaid: number; // In cents
  amountDue: number; // In cents
  dueDate: Date;
  daysOverdue: number;
  agingBucket: '0-30' | '31-60' | '61-90' | '90+';
  status: string;
}

export interface StudentProgressDetails {
  studentId: string;
  studentName: string;
  familyName: string;
  lessonsEnrolled: number;
  lessonsAttended: number;
  attendanceRate: number; // Percentage (0-100)
  instruments: string[]; // Array of instrument names
  programLevels: string[]; // Array of program level names
}

export interface StudentProgressRecord {
  studentId: string;
  studentName: string;
  familyName: string;
  lessonsEnrolled: number;
  lessonsAttended: number;
  attendanceRate: number; // Percentage (0-100)
  piecesAssigned: number;
  piecesCompleted: number;
  progressRate: number; // Percentage (0-100)
  currentStreak: number; // Days
  longestStreak: number; // Days
  currentLevel: string;
  instruments: string[]; // Array of instrument names
  programLevels: string[]; // Array of program level names
}

export interface StudentProgressByLevel {
  programLevelId: string;
  programLevelName: string;
  studentCount: number;
  averageProgressRate: number; // Percentage (0-100)
  averageAttendanceRate: number; // Percentage (0-100)
  totalPiecesCompleted: number;
}

export interface StudentPieceCompletion {
  pieceId: string;
  pieceName: string;
  totalAssigned: number;
  totalCompleted: number;
  completionRate: number; // Percentage (0-100)
  averageDaysToComplete: number;
}

export interface StudentStreakData {
  weekKey: string; // Format: "YYYY-Www"
  weekLabel: string; // Display format: "Week 10, 2026"
  averageStreak: number; // Average streak in days
  activeStudents: number;
  totalPracticeDays: number;
}

export interface TeacherPerformanceMetrics {
  teacherId: string;
  teacherName: string;
  lessonsDelivered: number;
  totalAttendanceRecords: number;
  averageAttendanceRate: number; // Percentage (0-100)
  uniqueStudents: number;
  instruments: string[]; // Array of instrument names taught
}

export interface TeacherPerformanceRecord {
  teacherId: string;
  teacherName: string;
  lessonsDelivered: number;
  totalAttendanceRecords: number;
  averageAttendanceRate: number; // Percentage (0-100)
  uniqueStudents: number;
  instruments: string[]; // Array of instrument names taught
  outstandingTasks: OutstandingTaskCounts;
}

export interface TeacherAttendanceMetrics {
  teacherId: string;
  teacherName: string;
  lessonsScheduled: number;
  totalAttendanceRecords: number;
  presentCount: number;
  absentCount: number;
  excusedCount: number;
  attendanceRate: number; // Percentage (0-100)
}

export interface TeacherRetentionData {
  teacherId: string;
  teacherName: string;
  totalEnrollments: number;
  activeStudents: number;
  retentionRate: number; // Percentage (0-100)
  averageStudentTenure: number; // Days
}

export interface TeacherRevenueData {
  teacherId: string;
  teacherName: string;
  totalRevenue: number; // In cents
  studentCount: number;
  revenuePerStudent: number; // In cents
  invoiceCount: number;
}

// ===========================================
// CHART DATA TYPES
// ===========================================

export interface AttendanceTrendData {
  weekKey: string; // Format: "YYYY-Www" (e.g., "2026-W10")
  weekLabel: string; // Display format: "Week 10, 2026"
  presentCount: number;
  absentCount: number;
  excusedCount: number;
  attendanceRate: number; // Percentage (0-100)
}

// Mirrors the backend's MRRTrendData (revenue.report.ts getMRRTrends).
export interface RevenueTrendData {
  month: string; // Format: "YYYY-MM" (e.g., "2026-03")
  monthLabel: string; // Display format: "Mar 2026"
  revenue: number; // In cents
  invoiceCount: number;
}

export interface GrowthTrendData {
  monthKey: string; // Format: "YYYY-MM"
  monthLabel: string; // Display format: "Mar 2026"
  activeStudents: number;
  newEnrollments: number;
  activeTeachers: number;
}

export interface PopularityBreakdown {
  name: string; // Instrument/Program Level name
  studentCount: number;
  lessonCount: number;
  percentage: number; // Percentage of total (0-100)
}

export interface PaymentMethodBreakdown {
  method: string; // 'STRIPE', 'CASH', 'BANK_TRANSFER', etc.
  totalAmount: number; // In cents
  invoiceCount: number;
  percentage: number; // Percentage of total revenue (0-100)
}

export interface HybridBookingMetricsSummary {
  totalHybridLessons: number;
  totalEnrolledStudents: number;
  totalBookingsMade: number;
  overallCompletionRate: number; // Percentage
  averageBookingTime: number; // Hours
  cancellationRate: number; // Percentage
  activeBookingWeeks: number;
}

export interface BookingCompletionByWeek {
  weekNumber: number;
  weekLabel: string; // "Week 3"
  enrolledStudents: number;
  bookedStudents: number;
  completionRate: number; // Percentage
  unbookedCount: number;
}

export interface TimeSlotData {
  dayOfWeek: string; // "Monday"
  timeSlot: string; // "14:30"
  bookingCount: number;
  utilizationRate: number; // Percentage
}

// What GET /reports/hybrid-booking/time-slots actually returns. `days`, `times`
// and `heatmapGrid` are a ready-made grid the current heatmap component ignores
// in favour of its own hardcoded DAYS/TIME_SLOTS constants.
export interface TimeSlotHeatmapData {
  timeSlots: TimeSlotData[];
  days: string[]; // ["Monday", "Tuesday", ...]
  times: string[]; // ["09:00", "09:30", ...]
  heatmapGrid: number[][]; // [day][time] = utilization rate
}

export interface BookingTrendRecord {
  weekNumber: number;
  weekLabel: string; // "Week 3"
  totalBookings: number;
  confirmedBookings: number;
  completionRate: number; // Percentage
  averageHoursToBook: number;
}

export interface CancellationRecord {
  weekNumber: number;
  weekLabel: string; // "Week 3"
  totalCancellations: number;
  cancellationRate: number; // Percentage
  topReasons: Array<{ reason: string; count: number }>;
}

export interface EnrollmentSummary {
  /** Enrolment ROWS — a student in two classes counts twice. Never label this "Students". */
  totalActiveEnrollments: number;
  /** DISTINCT actively-enrolled students (the headcount). Always <= totalActiveEnrollments. */
  totalActiveStudents: number;
  newEnrollmentsThisMonth: number;
  withdrawalsThisMonth: number;
  retentionRate: number; // Percentage
  averageEnrollmentDuration: number; // Days
}

export interface EnrollmentTrendRecord {
  month: string; // "2026-03"
  monthLabel: string; // "Mar 2026"
  activeEnrollments: number;
  newEnrollments: number;
  withdrawals: number;
  netGrowth: number;
}

export interface RetentionRecord {
  lessonId: string;
  lessonName: string;
  totalEnrollments: number;
  activeEnrollments: number;
  retentionRate: number; // Percentage
  averageTenureDays: number;
}

export interface ProgramLevelDistRecord {
  levelId: string;
  levelName: string; // "MINI", "MASTER", etc.
  studentCount: number;
  percentageOfTotal: number;
  averageProgress: number; // 0-100
}

export interface EnrollmentByTypeRecord {
  lessonTypeName: string;
  activeCount: number;
  percentage: number; // Percentage of total
}

// ===========================================
// OPERATIONS REPORT TYPES
// ===========================================

export interface OperationsFilters {
  termId?: string;
  startDate?: string;
  endDate?: string;
}

export interface RoomUtilization {
  roomId: string;
  roomName: string;
  locationName: string;
  totalAvailableHours: number;
  usedHours: number;
  utilizationRate: number;
}

export interface PeakHourData {
  hour: number;
  lessonCount: number;
  utilizationRate: number;
  isPeak: boolean;
}

export interface TeacherWorkload {
  teacherId: string;
  teacherName: string;
  totalLessons: number;
  totalHours: number;
  replacementCount: number;
  workloadScore: number;
  averageClassSize: number;
}

export interface ExtraLessonMetrics {
  totalRequests: number;
  approvedCount: number;
  rejectedCount: number;
  pendingCount: number;
  avgApprovalTimeHours: number;
  approvalRate: number;
}

export interface MeetGreetConversion {
  month: string;
  totalBookings: number;
  attendedCount: number;
  convertedCount: number;
  conversionRate: number;
  attendanceRate: number;
}

/** Mirrors the backend's `FunnelStage` / `EnquiryFunnelReport` (`reports.types.ts`, #371 Phase 7). */
export type EnquiryFunnelStage =
  | 'NEW' | 'IN_CONVERSATION' | 'MNG_BOOKED' | 'MNG_FOLLOW_UP' | 'READY_TO_ONBOARD' | 'ONBOARDING' | 'ENROLLED';

export interface EnquiryFunnelCounts {
  total: number;
  /** "Got at least this far", per stage the school uses. */
  reached: Partial<Record<EnquiryFunnelStage, number>>;
  waitlist: number;
  notProceeding: number;
  /** Enrolled / total, %. */
  conversionRate: number;
}

export interface EnquiryFunnel {
  stages: Array<{ stage: EnquiryFunnelStage; label: string }>;
  sideLabels: { waitlist: string; notProceeding: string };
  waitlistEnabled: boolean;
  months: Array<EnquiryFunnelCounts & { month: string; monthLabel: string }>;
  totals: EnquiryFunnelCounts;
  bySource: Array<{ id: string | null; label: string; total: number; enrolled: number; conversionRate: number }>;
  byClosedReason: Array<{ id: string | null; label: string; count: number }>;
  migrated: number;
  excluded: { spam: number; toReview: number };
  window: { from: string; to: string };
}

// Mirrors the backend's getARPS return (revenue.report.ts).
export interface ARPSData {
  averageRevenue: number; // In cents
  totalRevenue: number; // In cents
  totalStudents: number;
}

export interface RevenueByLessonType {
  lessonTypeName: string;
  totalRevenue: number; // In cents
  invoiceCount: number;
  percentage: number; // Percentage of total revenue (0-100)
}

export interface RevenueByTerm {
  termId: string;
  termName: string;
  totalRevenue: number; // In cents
  invoiceCount: number;
  startDate: string;
  endDate: string;
}

// ===========================================
// REPORT DATA RESPONSE TYPES
// ===========================================

export interface AttendanceReportData {
  summary: AttendanceReportSummary;
  breakdown: AttendanceReportRecord[];
  trends: AttendanceTrendData[];
  filters: AttendanceReportFilters;
}

// Mirrors what getRevenueReport actually returns (revenue.report.ts:169-174).
// This interface previously declared `outstandingInvoices` and `trends`, which
// the endpoint never sends, while omitting `byLessonType`/`byTerm`, which it
// does — so the Revenue Report page read undefined for both and could not reach
// the data it was actually given. Outstanding invoices come from their own
// endpoint (GET /reports/revenue/outstanding, useOutstandingInvoices).
export interface RevenueReportData {
  summary: RevenueReportSummary;
  byLessonType: RevenueByLessonType[];
  byTerm: RevenueByTerm[];
  filters: RevenueReportFilters;
}

export interface StudentProgressReportData {
  summary: StudentProgressSummary;
  students: StudentProgressRecord[];
  byLevel: StudentProgressByLevel[];
  pieceCompletion: StudentPieceCompletion[];
  streakTrends: StudentStreakData[];
  filters: StudentProgressFilters;
}

export interface TeacherPerformanceReportData {
  summary: TeacherPerformanceSummary;
  teachers: TeacherPerformanceRecord[];
  attendanceMetrics: TeacherAttendanceMetrics[];
  retentionData: TeacherRetentionData[];
  revenueData: TeacherRevenueData[];
  filters: TeacherPerformanceFilters;
}

// ===========================================
// EXPORT FORMAT TYPES
// ===========================================

export type ReportExportFormat = 'csv' | 'pdf';

// ===========================================
// RESPONSE WRAPPER TYPE
// ===========================================

interface ApiResponse<T> {
  status: string;
  data: T;
}

// ===========================================
// API FUNCTIONS
// ===========================================

export const reportsApi = {
  // ===========================================
  // ATTENDANCE REPORTS
  // ===========================================

  /**
   * Get comprehensive attendance report with filters
   * Returns summary statistics, breakdown by lesson, and weekly trends
   */
  getAttendanceReport: async (
    filters?: AttendanceReportFilters
  ): Promise<AttendanceReportData> => {
    const response = await apiClient.get<ApiResponse<AttendanceReportData>>(
      '/reports/attendance',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get attendance trends over time (weekly aggregation)
   * Returns array of weekly trend data with attendance rates
   */
  getAttendanceTrends: async (
    filters?: AttendanceReportFilters
  ): Promise<AttendanceTrendData[]> => {
    const response = await apiClient.get<ApiResponse<AttendanceTrendData[]>>(
      '/reports/attendance/trends',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Export attendance report as CSV or PDF
   * Returns blob data for file download
   */
  exportAttendanceReport: async (
    format: ReportExportFormat,
    filters?: AttendanceReportFilters
  ): Promise<Blob> => {
    const response = await apiClient.getRaw<Blob>('/reports/attendance/export', {
      params: { ...filters, format },
      responseType: 'blob', // CRITICAL for file downloads
    });
    return response.data;
  },

  // ===========================================
  // REVENUE REPORTS (Future Implementation)
  // ===========================================

  /**
   * Get comprehensive revenue report with filters
   * Returns summary statistics, outstanding invoices, and monthly trends
   */
  getRevenueReport: async (
    filters?: RevenueReportFilters
  ): Promise<RevenueReportData> => {
    const response = await apiClient.get<ApiResponse<RevenueReportData>>(
      '/reports/revenue',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get outstanding invoices with aging buckets
   * Returns array of outstanding invoices with days overdue
   */
  getOutstandingInvoices: async (): Promise<OutstandingInvoice[]> => {
    const response = await apiClient.get<ApiResponse<OutstandingInvoice[]>>(
      '/reports/revenue/outstanding'
    );
    return response.data;
  },

  /**
   * Get Monthly Recurring Revenue trends
   * Returns MRR data for the specified number of months
   */
  getMRRTrends: async (months: number = 12): Promise<RevenueTrendData[]> => {
    const response = await apiClient.get<ApiResponse<RevenueTrendData[]>>(
      '/reports/revenue/mrr',
      { params: { months } }
    );
    return response.data;
  },

  /**
   * Get Average Revenue Per Student
   * Returns ARPS for a specific term or overall
   */
  // `termId` is REQUIRED by the backend (arpsQuerySchema: z.string().min(1));
  // calling this without one is a 400. Returns an object, not a bare number.
  getARPS: async (termId: string): Promise<ARPSData> => {
    const response = await apiClient.get<ApiResponse<ARPSData>>(
      '/reports/revenue/arps',
      { params: { termId } }
    );
    return response.data;
  },

  /**
   * Get payment method breakdown
   * Returns distribution of payment methods used
   */
  getPaymentMethodBreakdown: async (termId?: string): Promise<PaymentMethodBreakdown[]> => {
    const response = await apiClient.get<ApiResponse<PaymentMethodBreakdown[]>>(
      '/reports/revenue/payment-methods',
      { params: { termId } }
    );
    return response.data;
  },

  /**
   * Export revenue report as CSV or PDF
   * Returns blob data for file download
   */
  exportRevenueReport: async (
    format: ReportExportFormat,
    filters?: RevenueReportFilters
  ): Promise<Blob> => {
    const response = await apiClient.getRaw<Blob>('/reports/revenue/export', {
      params: { ...filters, format },
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Export outstanding invoices as CSV or PDF
   * Returns blob data for file download
   */
  exportOutstandingInvoices: async (format: ReportExportFormat): Promise<Blob> => {
    const response = await apiClient.getRaw<Blob>('/reports/revenue/outstanding/export', {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  },

  // ===========================================
  // STUDENT PROGRESS REPORTS (Future Implementation)
  // ===========================================

  /**
   * Get comprehensive student progress report with filters
   * Returns summary statistics, individual student details, and breakdowns
   */
  getStudentProgressReport: async (
    filters?: StudentProgressFilters
  ): Promise<StudentProgressReportData> => {
    const response = await apiClient.get<ApiResponse<StudentProgressReportData>>(
      '/reports/student-progress',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get student progress grouped by program level
   * Returns progress statistics for each program level
   */
  getStudentProgressByLevel: async (termId?: string): Promise<StudentProgressByLevel[]> => {
    const response = await apiClient.get<ApiResponse<StudentProgressByLevel[]>>(
      '/reports/student-progress/by-level',
      { params: { termId } }
    );
    return response.data;
  },

  /**
   * Get piece completion rates
   * Returns statistics on how many pieces are completed vs assigned
   */
  getStudentPieceCompletionRates: async (
    filters?: { termId?: string; instrumentId?: string }
  ): Promise<StudentPieceCompletion[]> => {
    const response = await apiClient.get<ApiResponse<StudentPieceCompletion[]>>(
      '/reports/student-progress/piece-completion',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get student streak analysis over time
   * Returns weekly streak trend data
   */
  getStudentStreakAnalysis: async (termId?: string): Promise<StudentStreakData[]> => {
    const response = await apiClient.get<ApiResponse<StudentStreakData[]>>(
      '/reports/student-progress/streaks',
      { params: { termId } }
    );
    return response.data;
  },

  /**
   * Export student progress report as CSV or PDF
   * Returns blob data for file download
   */
  exportStudentProgressReport: async (
    format: ReportExportFormat,
    filters?: StudentProgressFilters
  ): Promise<Blob> => {
    const response = await apiClient.getRaw<Blob>('/reports/student-progress/export', {
      params: { ...filters, format },
      responseType: 'blob',
    });
    return response.data;
  },

  // ===========================================
  // TEACHER PERFORMANCE REPORTS (Future Implementation)
  // ===========================================

  /**
   * Get comprehensive teacher performance report with filters
   * Returns summary statistics and individual teacher metrics
   */
  getTeacherPerformanceReport: async (
    filters?: TeacherPerformanceFilters
  ): Promise<TeacherPerformanceReportData> => {
    const response = await apiClient.get<ApiResponse<TeacherPerformanceReportData>>(
      '/reports/teacher-performance',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get teacher attendance metrics
   * Returns attendance statistics for teachers
   */
  getTeacherAttendanceMetrics: async (
    filters?: { teacherId?: string; termId?: string }
  ): Promise<TeacherAttendanceMetrics[]> => {
    const response = await apiClient.get<ApiResponse<TeacherAttendanceMetrics[]>>(
      '/reports/teacher-performance/attendance',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get teacher student retention rates
   * Returns retention statistics for teachers
   */
  getTeacherStudentRetention: async (
    filters?: { teacherId?: string; termId?: string }
  ): Promise<TeacherRetentionData[]> => {
    const response = await apiClient.get<ApiResponse<TeacherRetentionData[]>>(
      '/reports/teacher-performance/retention',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get teacher revenue contribution
   * Returns revenue attribution for teachers
   */
  getTeacherRevenueContribution: async (
    filters?: { teacherId?: string; termId?: string }
  ): Promise<TeacherRevenueData[]> => {
    const response = await apiClient.get<ApiResponse<TeacherRevenueData[]>>(
      '/reports/teacher-performance/revenue',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Export teacher performance report as CSV or PDF
   * Returns blob data for file download
   */
  exportTeacherPerformanceReport: async (
    format: ReportExportFormat,
    filters?: TeacherPerformanceFilters
  ): Promise<Blob> => {
    const response = await apiClient.getRaw<Blob>('/reports/teacher-performance/export', {
      params: { ...filters, format },
      responseType: 'blob',
    });
    return response.data;
  },

  // ===========================================
  // HYBRID BOOKING REPORTS
  // ===========================================

  /**
   * Get hybrid booking metrics summary
   * Returns overall statistics for hybrid booking system
   */
  getHybridBookingMetrics: async (
    filters?: HybridBookingReportFilters
  ): Promise<{ summary: HybridBookingMetricsSummary; completionByWeek: BookingCompletionByWeek[]; filters: HybridBookingReportFilters }> => {
    const response = await apiClient.get<ApiResponse<{ summary: HybridBookingMetricsSummary; completionByWeek: BookingCompletionByWeek[]; filters: HybridBookingReportFilters }>>(
      '/reports/hybrid-booking/metrics',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get booking completion rates by week
   * Returns week-by-week completion statistics
   */
  getBookingCompletionRates: async (
    filters?: HybridBookingReportFilters
  ): Promise<BookingCompletionByWeek[]> => {
    // The route is /completion-RATES; calling /completion 404s (this was the
    // 404 in the console on the Hybrid Booking Analytics page).
    const response = await apiClient.get<ApiResponse<BookingCompletionByWeek[]>>(
      '/reports/hybrid-booking/completion-rates',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get time slot popularity heatmap data
   * Returns booking distribution across days and time slots
   */
  // The endpoint returns a WRAPPER (TimeSlotHeatmapData), not a bare array:
  // { timeSlots, days, times, heatmapGrid }. Declaring it as an array made
  // TimeSlotHeatmap call `.find` on an object and crash the whole page with
  // "data.find is not a function". Unwrap here so callers get the array they expect.
  getTimeSlotPopularity: async (
    filters?: HybridBookingReportFilters
  ): Promise<TimeSlotData[]> => {
    const response = await apiClient.get<ApiResponse<TimeSlotHeatmapData>>(
      '/reports/hybrid-booking/time-slots',
      { params: filters }
    );
    return response.data?.timeSlots ?? [];
  },

  /**
   * Get booking trends over time
   * Returns week-by-week booking patterns
   */
  // Also a WRAPPER ({ trends, filters }), not a bare array — the same defect as
  // getTimeSlotPopularity above. BookingTrendsChart calls `.length` then `.map`,
  // so this was a second crash on the same page; the heatmap simply threw first.
  getBookingTrends: async (
    filters?: HybridBookingReportFilters
  ): Promise<BookingTrendRecord[]> => {
    const response = await apiClient.get<ApiResponse<{ trends: BookingTrendRecord[]; filters: HybridBookingReportFilters }>>(
      '/reports/hybrid-booking/trends',
      { params: filters }
    );
    return response.data?.trends ?? [];
  },

  /**
   * Get cancellation patterns analysis
   * Returns cancellation statistics and reasons
   */
  getCancellationPatterns: async (
    filters?: HybridBookingReportFilters
  ): Promise<{ summary: { totalCancellations: number; overallCancellationRate: number }; byWeek: CancellationRecord[]; filters: HybridBookingReportFilters }> => {
    const response = await apiClient.get<ApiResponse<{ summary: { totalCancellations: number; overallCancellationRate: number }; byWeek: CancellationRecord[]; filters: HybridBookingReportFilters }>>(
      '/reports/hybrid-booking/cancellations',
      { params: filters }
    );
    return response.data;
  },

  // ===========================================
  // ENROLLMENT REPORTS
  // ===========================================

  /**
   * Get enrollment trends over time
   * Returns monthly enrollment statistics
   */
  getEnrollmentTrends: async (months: number = 12): Promise<{ summary: EnrollmentSummary; monthlyTrends: EnrollmentTrendRecord[] }> => {
    const response = await apiClient.get<ApiResponse<{ summary: EnrollmentSummary; monthlyTrends: EnrollmentTrendRecord[] }>>(
      '/reports/enrollment/trends',
      { params: { months } }
    );
    return response.data;
  },

  /**
   * Get student retention rates
   * Returns retention statistics by lesson and teacher
   */
  getRetentionRates: async (
    filters?: EnrollmentReportFilters
  ): Promise<{ overallRetentionRate: number; byLesson: RetentionRecord[]; byTeacher: Array<{ teacherId: string; teacherName: string; retentionRate: number; activeStudents: number }>; filters: EnrollmentReportFilters }> => {
    const response = await apiClient.get<ApiResponse<{ overallRetentionRate: number; byLesson: RetentionRecord[]; byTeacher: Array<{ teacherId: string; teacherName: string; retentionRate: number; activeStudents: number }>; filters: EnrollmentReportFilters }>>(
      '/reports/enrollment/retention',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get program level distribution
   * Returns student distribution across program levels
   */
  getProgramLevelDistribution: async (): Promise<{ byLevel: ProgramLevelDistRecord[]; totalStudents: number }> => {
    const response = await apiClient.get<ApiResponse<{ byLevel: ProgramLevelDistRecord[]; totalStudents: number }>>(
      '/reports/enrollment/program-levels'
    );
    return response.data;
  },

  /**
   * Get enrollment distribution by lesson type
   * Returns enrollment breakdown by type (HYBRID, INDIVIDUAL, GROUP, BAND)
   */
  getEnrollmentByLessonType: async (
    filters?: EnrollmentReportFilters
  ): Promise<{ byType: EnrollmentByTypeRecord[]; totalEnrollments: number; filters: EnrollmentReportFilters }> => {
    const response = await apiClient.get<ApiResponse<{ byType: EnrollmentByTypeRecord[]; totalEnrollments: number; filters: EnrollmentReportFilters }>>(
      '/reports/enrollment/by-type',
      { params: filters }
    );
    return response.data;
  },

  // ===========================================
  // OPERATIONS REPORTS
  // ===========================================

  /**
   * Get room utilization metrics
   * Returns room usage statistics with utilization rates
   */
  getRoomUtilization: async (filters?: OperationsFilters): Promise<RoomUtilization[]> => {
    const response = await apiClient.get<ApiResponse<RoomUtilization[]>>(
      '/reports/operations/room-utilization',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get peak hour booking patterns
   * Returns lesson density by hour of day
   */
  getPeakHours: async (filters?: OperationsFilters): Promise<PeakHourData[]> => {
    const response = await apiClient.get<ApiResponse<PeakHourData[]>>(
      '/reports/operations/peak-hours',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get teacher workload distribution
   * Returns teacher workload metrics and balance
   */
  getTeacherWorkload: async (filters?: OperationsFilters): Promise<TeacherWorkload[]> => {
    const response = await apiClient.get<ApiResponse<TeacherWorkload[]>>(
      '/reports/operations/teacher-workload',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get extra lesson request metrics
   * Returns approval rates and processing times
   */
  getExtraLessonMetrics: async (filters?: OperationsFilters): Promise<ExtraLessonMetrics> => {
    const response = await apiClient.get<ApiResponse<ExtraLessonMetrics>>(
      '/reports/operations/extra-lesson-metrics',
      { params: filters }
    );
    return response.data;
  },

  /**
   * Get meet & greet conversion funnel
   * Returns booking to enrollment conversion rates
   */
  getMeetGreetConversion: async (months: number = 12): Promise<MeetGreetConversion[]> => {
    const response = await apiClient.get<ApiResponse<MeetGreetConversion[]>>(
      '/reports/operations/meet-greet-conversion',
      { params: { months } }
    );
    return response.data;
  },

  getEnquiryFunnel: async (months: number = 12): Promise<EnquiryFunnel> => {
    const response = await apiClient.get<ApiResponse<EnquiryFunnel>>(
      '/reports/operations/enquiry-funnel',
      { params: { months } }
    );
    return response.data;
  },
};

// ===========================================
// EMAIL LOG REPORT (2026-09-09)
// ===========================================
// Mirrors apps/backend/src/services/reports/reports.types.ts (Dates arrive as strings).

/**
 * Mirror of the Prisma `EmailCategory` enum. Add in BOTH places.
 *
 * ⚠️ THIS LIST HAD ALREADY DRIFTED. `REPORTS` shipped on 2026-09-16 and was
 * never mirrored, so the Email Log report's Type filter could not select Report
 * Digests at all — the comment above said "add in BOTH places" and nothing
 * enforced it. `emailCategoryParity.test.ts` now reads the backend enum and
 * fails when the two disagree, which is what the comment alone could not do.
 */
export const EMAIL_CATEGORY_OPTIONS = [
  { value: 'ACCOUNT', label: 'Account' },
  { value: 'MEET_AND_GREET', label: 'Meet & Greet' },
  { value: 'INVOICING', label: 'Invoicing' },
  { value: 'LESSONS', label: 'Lessons' },
  { value: 'HYBRID_BOOKING', label: 'Hybrid Booking' },
  { value: 'ENQUIRY', label: 'Enquiry' },
  { value: 'EXTRA_LESSONS', label: 'Extra Lessons' },
  { value: 'ABSENCES', label: 'Absences' },
  { value: 'TEACHERS', label: 'Teachers' },
  { value: 'SECURITY', label: 'Security' },
  { value: 'ACCOUNT_DELETION', label: 'Account Deletion' },
  { value: 'SUBSCRIPTIONS', label: 'Subscriptions' },
  { value: 'GAMIFICATION', label: 'Gamification' },
  { value: 'NEWSLETTER', label: 'Newsletter' },
  { value: 'MESSAGING', label: 'Messaging' },
  { value: 'INVITATIONS', label: 'Invitations' },
  { value: 'STAFF', label: 'Staff' },
  { value: 'ONBOARDING', label: 'Onboarding' },
  { value: 'EVENTS', label: 'Events' },
  { value: 'CUSTOM_EMAIL', label: 'Custom Emails' },
  { value: 'REPORTS', label: 'Report Digests' },
  { value: 'EMAIL_SEQUENCES', label: 'Email Sequences' },
] as const;
export type EmailCategoryValue = (typeof EMAIL_CATEGORY_OPTIONS)[number]['value'];

export const EMAIL_SEND_OUTCOME_OPTIONS = [
  { value: 'ACCEPTED', label: 'Sent (accepted by Mailgun)' },
  { value: 'NOT_CONFIGURED', label: 'Not sent: Mailgun not configured' },
  { value: 'REJECTED', label: 'Not sent: rejected' },
  { value: 'RETRIES_EXHAUSTED', label: 'Not sent: retries exhausted' },
] as const;
export type EmailSendOutcomeValue = (typeof EMAIL_SEND_OUTCOME_OPTIONS)[number]['value'];

export const EMAIL_STATUS_OPTIONS = [
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'SENT', label: 'Sent, not yet delivered' },
  { value: 'BOUNCED', label: 'Bounced' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'PENDING', label: 'Pending' },
] as const;
export type EmailDeliveryStatusValue = 'PENDING' | 'QUEUED' | 'SENT' | 'DELIVERED' | 'FAILED' | 'BOUNCED';

export interface EmailLogReportFilters {
  /** YYYY-MM-DD, a school-local day (inclusive). */
  startDate?: string;
  endDate?: string;
  emailCategory?: EmailCategoryValue;
  sendOutcome?: EmailSendOutcomeValue;
  status?: EmailDeliveryStatusValue;
  recipientEmail?: string;
  page?: number;
  pageSize?: number;
}

export interface EmailLogSummary {
  total: number;
  accepted: number;
  notSent: number;
  notSentByOutcome: Record<'NOT_CONFIGURED' | 'REJECTED' | 'RETRIES_EXHAUSTED' | 'LEGACY_FAILED', number>;
  pending: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
  failedStatus: number;
  deliveryRate: number;
  openRate: number;
  /**
   * Mail still to go out RIGHT NOW — a snapshot of the present, deliberately
   * NOT filtered by the report's date range (a campaign started last week is
   * still sending today).
   *
   * OPTIONAL on purpose: a frontend deployed ahead of the backend (or a cached
   * response from before this shipped) has no such field, and the card must
   * simply not render rather than crash on `undefined.emailsQueued`.
   */
  inFlight?: {
    campaignsQueued: number;
    campaignsSending: number;
    emailsQueued: number;
  };
  periodStart: string | null;
  periodEnd: string | null;
  timeZone: string;
}

export interface EmailLogCategoryRow {
  emailCategory: EmailCategoryValue | null;
  label: string;
  total: number;
  accepted: number;
  notSent: number;
  delivered: number;
  opened: number;
  bounced: number;
  complained: number;
}

export interface EmailLogRow {
  id: string;
  createdAt: string;
  recipientEmail: string | null;
  subject: string | null;
  emailCategory: EmailCategoryValue | null;
  categoryLabel: string;
  templateKey: string | null;
  sendOutcome: EmailSendOutcomeValue | null;
  status: EmailDeliveryStatusValue;
  sentAt: string | null;
  deliveredAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  bouncedAt: string | null;
  complainedAt: string | null;
  failedAt: string | null;
  providerError: string | null;
}

export interface EmailLogReportData {
  summary: EmailLogSummary;
  byCategory: EmailLogCategoryRow[];
  rows: EmailLogRow[];
  pagination: { page: number; pageSize: number; total: number; pageCount: number };
}

export const emailLogReportApi = {
  get: async (filters?: EmailLogReportFilters): Promise<EmailLogReportData> => {
    const response = await apiClient.get<ApiResponse<EmailLogReportData>>('/reports/email-log', { params: filters });
    return response.data;
  },
  export: async (format: ReportExportFormat, filters?: EmailLogReportFilters): Promise<Blob> => {
    const response = await apiClient.getRaw<Blob>('/reports/email-log/export', {
      params: { ...filters, format },
      responseType: 'blob',
    });
    return response.data;
  },
};

// ===========================================
// RETRO-EDIT REPORT (2026-09-18)
// ===========================================
// The supervision surface over the retro-edit audit log.
//
// ⚠️ THESE INTERFACES WERE DIFFED FIELD-BY-FIELD against what
// `apps/backend/src/services/reports/retro-edits.report.ts` actually returns,
// not copied from its types. `apiClient.get<ApiResponse<T>>` is a type
// ASSERTION over an untyped JSON body — `T` is a claim about the server, never
// a check of it (CLAUDE.md, 2026-09-15: seven asserted fields once rendered
// `$NaN` in production). Two deliberate divergences from the backend types:
//
//   • `periodStart` / `periodEnd` / `performedAt` / `editedDate` are `Date` on
//     the backend and STRINGS here. They cross the wire as JSON; nothing
//     revives them. Declaring them `Date` would type-check and then throw on
//     `.getTime()`.
//   • `details` is `unknown`, so a consumer must narrow before reading it.

export interface RetroEditsReportFilters {
  /** YYYY-MM-DD, a school-local day (inclusive). */
  startDate?: string;
  endDate?: string;
  performedById?: string;
  action?: string;
  entityType?: string;
  /** "Everything recorded more than N days late" — the owner's sharpest filter. */
  minLagDays?: number;
  page?: number;
  pageSize?: number;
}

export interface RetroEditsSummary {
  totalEdits: number;
  totalDeletions: number;
  /** `lagDays > 0` — the stored answer, decided at write time on the school's day. */
  retroactiveEdits: number;
  medianLagDays: number;
  worstLagDays: number;
  /** ISO strings, not Dates — see the note above. */
  periodStart: string | null;
  periodEnd: string | null;
  /** The zone the period was cut in. Always a valid IANA zone. */
  timeZone: string;
  /**
   * HOW that zone was resolved. ⚠️ BRANCH ON THIS, NEVER ON `timeZone`.
   * A missing zone is masked as the product default before the backend's
   * guard sees it, and `'UTC'` is itself a legitimate zone — so the string
   * alone cannot tell a problem from a correct configuration. Phase 6
   * shipped a banner keyed on `timeZone === 'UTC'` and it was wrong in BOTH
   * directions: silent for a school with no zone, and falsely alarming for
   * one genuinely running on UTC.
   */
  timeZoneSource: 'configured' | 'missing' | 'invalid';
  /** What was actually stored, so an 'invalid' warning can name it. */
  timeZoneStored: string | null;
}

/** One person's behaviour over the period. THE HEADLINE of this report. */
export interface RetroEditActorSummary {
  /** Null for a hard-deleted admin — the name still identifies them. */
  performedById: string | null;
  performedByName: string;
  editCount: number;
  deletionCount: number;
  retroactiveCount: number;
  medianLagDays: number;
  worstLagDays: number;
}

/** One audit row — the drill-down beneath the per-person table. */
export interface RetroEditRow {
  id: string;
  performedAt: string;
  performedById: string | null;
  performedByName: string;
  action: string;
  entityType: string;
  entityId: string;
  /** The calendar day whose record was changed (UTC midnight, format with UTC getters). */
  editedDate: string;
  lagDays: number;
  reason: string | null;
  /** `{ before, after }`, plus `lagDaysDegraded` / `actorNameUnresolved` when set. */
  details: unknown;
}

export interface RetroEditsReportData {
  summary: RetroEditsSummary;
  byActor: RetroEditActorSummary[];
  rows: RetroEditRow[];
  pagination: { page: number; pageSize: number; total: number; pageCount: number };
}

export const retroEditsReportApi = {
  get: async (filters?: RetroEditsReportFilters): Promise<RetroEditsReportData> => {
    const response = await apiClient.get<ApiResponse<RetroEditsReportData>>('/reports/retro-edits', { params: filters });
    return response.data;
  },
  /**
   * CSV = every row in the period; PDF = summary + the per-person table.
   *
   * Added in Phase 8. Phase 6 deliberately shipped WITHOUT this method because
   * the backend route did not exist yet — a client method pointing at a missing
   * endpoint type-checks, tests green, and 404s on the first click.
   */
  export: async (format: ReportExportFormat, filters?: RetroEditsReportFilters): Promise<Blob> => {
    const response = await apiClient.getRaw<Blob>('/reports/retro-edits/export', {
      params: { ...filters, format },
      responseType: 'blob',
    });
    return response.data;
  },
};

// ===========================================
// #374 Phase 2: registration fees not yet applied (registration_fees.read)
// ===========================================
// A live snapshot (no filters). Dates are YYYY-MM-DD school days: show them dd/mm/yyyy.
// Registered in 4 of the nine report places on purpose (the #642 family credit report's shape):
// this client, the hook, the nav entry + route guard; the Overview card, `reportKeys` and the
// digest are a follow-up.

export type RegistrationFeeMatch = 'FULL_NAME' | 'FIRST_NAME' | 'ONLY_CHILD';

/** Id and number are null without `operations_invoices.read`: the ref still says "an invoice exists". */
export interface NotAppliedInvoiceRef {
  invoiceId: string | null;
  invoiceNumber: string | null;
}

export interface NotAppliedFeeRow {
  feeId: string;
  studentId: string | null;
  studentName: string | null;
  familyId: string;
  familyName: string;
  /** For the family page's `?parent=` deep link. */
  parentId: string | null;
  /** YYYY-MM-DD: the school day the fee was paid. */
  paidOn: string;
  /** "50.00" */
  amount: string;
  daysHeld: number;
  studentMatch: RegistrationFeeMatch | null;
  /** null = "No current enrolment". */
  nextTerm: { id: string; name: string } | null;
  /**
   * A live invoice already bills ALL this child's `nextTerm` enrolments. `creditable`: a DRAFT the child is linked to
   * with lines left, so the credit can be ticked in Edit; otherwise the fee comes off the NEXT invoice by itself.
   */
  alreadyInvoiced: (NotAppliedInvoiceRef & { creditable: boolean }) | null;
  /** The fee already carries a live credit (Check / Unassigned rows): confirm the child, not move it. */
  credit: NotAppliedInvoiceRef | null;
  /** False for a withdrawn / removed / inactive child ("Child not active", not "No current enrolment"). */
  childActive: boolean;
  /** What the credit is worth: the fee capped at the school's fee (a #655 row stores the card fee too). */
  creditAmount: string;
  /** The family's active children (Check / Unassigned rows), for Assign to child. */
  children: { id: string; name: string }[];
  /** P7: Stripe refunded this fee and the flag is not cleared (ISO instant). */
  refundFlag: { at: string; amount: string } | null;
}

export interface NotLinkedFeeRow {
  feeId: string;
  childName: string | null;
  meetAndGreetStatus: string | null;
  paidOn: string;
  amount: string;
  /** P7: Stripe refunded this unlinked fee (ISO instant). */
  refundFlag: { at: string; amount: string } | null;
}

export interface RegistrationFeesNotAppliedReport {
  creditOnFirstInvoice: boolean;
  creditFeesPaidFrom: string | null;
  /** YYYY-MM-DD: the school's day. */
  asOf: string;
  held: NotAppliedFeeRow[];
  check: NotAppliedFeeRow[];
  unassigned: NotAppliedFeeRow[];
  refundFlagged: NotAppliedFeeRow[];
  notLinked: NotLinkedFeeRow[];
  totals: {
    held: number;
    heldAmount: string;
    heldAlreadyInvoiced: number;
    check: number;
    unassigned: number;
    refundFlagged: number;
    notLinked: number;
  };
}

export const registrationFeesNotAppliedApi = {
  get: async (): Promise<RegistrationFeesNotAppliedReport> =>
    (await apiClient.get<ApiResponse<RegistrationFeesNotAppliedReport>>('/reports/registration-fees/not-applied')).data,
  exportCsv: async (): Promise<Blob> =>
    (await apiClient.getRaw<Blob>('/reports/registration-fees/not-applied/export', { responseType: 'blob' })).data,
};

// ===========================================
// #374 P6: registration fees paid in a week
// ===========================================

export type WeeklyFeeState = 'HELD' | 'APPLIED' | 'NOT_CREDITED' | 'FORFEITED' | 'REFUNDED' | 'UNASSIGNED' | 'NOT_LINKED' | 'REFUND_FLAGGED';

export interface WeeklyFeeRow {
  feeId: string;
  studentId: string | null;
  childName: string | null;
  familyId: string | null;
  familyName: string | null;
  parentId: string | null;
  /** YYYY-MM-DD: the school day the fee was paid. */
  paidOn: string;
  amount: string;
  method: string;
  state: WeeklyFeeState;
  /** Ids/numbers are null without operations_invoices.read. */
  appliedInvoice: { invoiceId: string | null; invoiceNumber: string | null } | null;
  forfeitReason: string | null;
  forfeitDetail: string | null;
  /** P7: Stripe refunded the fee (ISO instant); `active` = not cleared. Also on Applied rows. */
  refundFlag: { at: string; amount: string; active: boolean } | null;
}

export interface RegistrationFeesWeeklyReport {
  period: { from: string; to: string };
  isDefaultWeek: boolean;
  /** YYYY-MM-DD: the school's today. */
  asOf: string;
  creditOnFirstInvoice: boolean;
  rows: WeeklyFeeRow[];
  totals: {
    count: number;
    amount: string;
    byMethod: { method: string; count: number; amount: string }[];
    byState: { state: WeeklyFeeState; count: number; amount: string }[];
  };
}

/** Both dates or neither (neither = the server's last complete Mon-Sun week). */
export interface RegistrationFeesWeeklyParams {
  from?: string;
  to?: string;
}

export const registrationFeesWeeklyApi = {
  get: async (params: RegistrationFeesWeeklyParams = {}): Promise<RegistrationFeesWeeklyReport> =>
    (await apiClient.get<ApiResponse<RegistrationFeesWeeklyReport>>('/reports/registration-fees/weekly', { params })).data,
  exportCsv: async (params: RegistrationFeesWeeklyParams = {}): Promise<Blob> =>
    (await apiClient.getRaw<Blob>('/reports/registration-fees/weekly/export', { params, responseType: 'blob' })).data,
};

// ===========================================
// #374 P8: registration fees kept (Not proceeding), by financial year
// ===========================================

export type ForfeitMovementKind = 'FORFEITED' | 'REVERSED' | 'REFUNDED';

export interface ForfeitMovementRow {
  id: string;
  feeId: string;
  kind: ForfeitMovementKind;
  /** YYYY-MM-DD: the school day of the movement. */
  day: string;
  childName: string | null;
  familyId: string | null;
  familyName: string | null;
  parentId: string | null;
  reason: string | null;
  feeAmount: string | null;
  refunded: string | null;
  /** Signed money string: a reversal or a later refund is negative. */
  kept: string;
}

export interface RegistrationFeesForfeitedReport {
  fy: number;
  label: string;
  period: { from: string; to: string };
  /** Newest first, for the picker. */
  years: { fy: number; label: string }[];
  rows: ForfeitMovementRow[];
  totals: {
    forfeited: number;
    reversed: number;
    /** Net kept in the year (signed). */
    kept: string;
    /** The year's forfeits, and what reversals and refunds took back (a positive amount). */
    keptGross: string;
    takenBack: string;
    /** What the year's figures leave out of kept for Stripe refunds. */
    refunded: string;
  };
}

/** No fy = the school's current financial year. */
export interface RegistrationFeesForfeitedParams {
  fy?: number;
}

export const registrationFeesForfeitedApi = {
  get: async (params: RegistrationFeesForfeitedParams = {}): Promise<RegistrationFeesForfeitedReport> =>
    (await apiClient.get<ApiResponse<RegistrationFeesForfeitedReport>>('/reports/registration-fees/forfeited', { params })).data,
  exportCsv: async (params: RegistrationFeesForfeitedParams = {}): Promise<Blob> =>
    (await apiClient.getRaw<Blob>('/reports/registration-fees/forfeited/export', { params, responseType: 'blob' })).data,
};
